import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Plus, Building2, Users, PhoneCall, Calendar, ExternalLink, Loader2 } from "lucide-react";

const STATUS_TINT = {
  active: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
  paused: "bg-amber-500/10 border-amber-500/30 text-amber-300",
  archived: "bg-white/5 border-white/10 text-white/40",
};

export default function Clients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/clients").then(({ data }) => {
      setItems(data.items || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFB000] font-medium mb-2">White-Label</div>
          <h1 className="text-3xl font-bold tracking-tight">Clients</h1>
          <p className="text-sm text-white/50 mt-2">Each client gets their own branded portal — calls, appointments, and performance.</p>
        </div>
        <button
          onClick={() => navigate("/clients/new")}
          data-testid="new-client-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-black bg-[#FFB000] hover:bg-[#FFC633] transition-colors shadow-lg shadow-[#FFB000]/20"
        >
          <Plus className="w-4 h-4" /> New client
        </button>
      </div>

      {loading ? (
        <div className="text-white/50 text-sm py-12 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-[#121214] border border-white/5 rounded-2xl p-12 text-center" data-testid="clients-empty">
          <Building2 className="w-10 h-10 text-white/20 mx-auto mb-4" strokeWidth={1.5} />
          <div className="text-white/70 font-medium">No clients yet</div>
          <div className="text-white/40 text-sm mt-1 mb-5">Create your first client to give them a branded portal.</div>
          <button
            onClick={() => navigate("/clients/new")}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-black bg-[#FFB000] hover:bg-[#FFC633]"
          >
            <Plus className="w-4 h-4" /> Create client
          </button>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((c) => (
            <Link
              key={c.id}
              to={`/clients/${c.id}`}
              data-testid={`client-row-${c.id}`}
              className="bg-[#121214] border border-white/5 rounded-2xl p-5 hover:border-white/15 transition-colors block"
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden border border-white/10"
                  style={{ background: c.logo_url ? "transparent" : `${c.accent_color}20` }}
                >
                  {c.logo_url ? (
                    <img src={c.logo_url} alt="" className="w-full h-full object-contain" />
                  ) : (
                    <Building2 className="w-5 h-5" style={{ color: c.accent_color }} strokeWidth={1.75} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <div className="text-base font-semibold text-white truncate">{c.name}</div>
                    <span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded border ${STATUS_TINT[c.status] || STATUS_TINT.active} font-medium`}>
                      {c.status}
                    </span>
                  </div>
                  <div className="text-[12px] text-white/45 mt-1 flex items-center gap-3 flex-wrap">
                    <span>/portal/{c.slug}</span>
                    <span>·</span>
                    <span>{c.contact_name}</span>
                    {c.industry && <><span>·</span><span>{c.industry}</span></>}
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-5 text-xs text-white/55">
                  <div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {c.stats?.users || 0}</div>
                  <div className="flex items-center gap-1.5"><PhoneCall className="w-3.5 h-3.5" /> {c.stats?.calls || 0}</div>
                  <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {c.stats?.appointments || 0}</div>
                </div>
                <a
                  href={`/portal/${c.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-[#FFB000] hover:text-[#FFC633]"
                  title="Open portal"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
